import { useEffect, useRef } from 'react';
import type { FC } from 'react';

import { useActivityCenterTracking } from '@xing-com/crate-activity-center-tracking';
import {
  useActivityCenterButtonRendered,
  useActivityCenterTotal,
  useIsActivityCenterVisible,
  useSetActivityCenterVisible,
} from '@xing-com/hub';

import * as Styled from './activity-center-button.styled';
import { SuperEllipseWithBadge } from './super-ellipse-with-badge';

type ActivityCenterButtonProps = {
  title: string;
  href?: string;
};

export const ActivityCenterButton: FC<ActivityCenterButtonProps> = ({
  title,
  href,
}) => {
  useActivityCenterButtonRendered();

  const total = useActivityCenterTotal();
  const setActivityCenterVisible = useSetActivityCenterVisible();
  const isActivityCenterVisible = useIsActivityCenterVisible();
  const { trackInteraction } = useActivityCenterTracking();

  const justClosedRef = useRef<boolean>(false);

  useEffect(() => {
    if (isActivityCenterVisible) return;

    justClosedRef.current = true;

    const timerId = setTimeout(() => {
      justClosedRef.current = false;
    }, 100);

    return () => {
      clearTimeout(timerId);
      justClosedRef.current = false;
    };
  }, [isActivityCenterVisible]);

  return (
    <Styled.Link
      id="activity-center-button"
      data-testid="frame-vnav-activity-center-button"
      to={href}
      aria-label={title}
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();

        if (!justClosedRef.current) {
          setActivityCenterVisible(!isActivityCenterVisible);
        }

        trackInteraction({
          event: 'clicked',
          element: 'activitycenter',
          elementDetail: 'activitycenter_open',
        });
      }}
    >
      <SuperEllipseWithBadge count={total} />
    </Styled.Link>
  );
};
